import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { CloseIcon } from "stream-chat-react";

/**
 * PaymentStatusModal
 * Renders one of four post-payment states.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - status: "topUpSuccess" | "paySendSuccess" | "paymentFailed" | "paidButSendFailed"
 *  - data: {
 *      amountPaid,       // e.g. 98
 *      creditsAdded,     // e.g. 2
 *      creditsUsed,      // e.g. 1 (paySendSuccess)
 *      newBalance,       // e.g. 14
 *      currentBalance,   // e.g. 2 (paidButSendFailed)
 *    }
 *  - onPrimaryAction: () => void   // Retry payment / Try sending again / Done
 *  - onSecondaryAction: () => void // Cancel / Close
 */

const CheckIcon = () => (
  <Box
    sx={{
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      backgroundColor: "#2e9e5b",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "26px",
      margin: "0 auto 16px",
    }}
  >
    ✓
  </Box>
);

const WarningIcon = ({ color, bg }) => (
  <Box
    sx={{
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      backgroundColor: bg,
      color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px",
      fontWeight: 700,
      margin: "0 auto 16px",
    }}
  >
    !
  </Box>
);

const PaymentStatusModal = ({
  open,
  onClose,
  status,
  data = {},
  onPrimaryAction,
  onSecondaryAction,
}) => {
  const {
    amountPaid,
    creditsAdded,
    creditsUsed,
    newBalance,
    currentBalance,
  } = data;

  const renderContent = () => {
    switch (status) {
      case "topUpSuccess":
        return (
          <Box
            sx={{
              backgroundColor: "#f2fbf5",
              border: "1px solid #cdeddc",
              borderRadius: "11px",
              padding: "14px 16px",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              <Box
                sx={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: "#2e9e5b",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                  flexShrink: 0,
                  marginTop: "1px",
                }}
              >
                ✓
              </Box>
              <Box>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#22223b" }}>
                  Payment successful
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#4c4a5c", marginTop: "2px" }}>
                  ₹{amountPaid} paid · {creditsAdded} credits added
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#4c4a5c" }}>
                  New balance: {newBalance} credits
                </Typography>
              </Box>
            </Box>

            <IconButton onClick={onClose} size="small" sx={{ color: "#777", padding: "2px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        );

      case "paySendSuccess":
        return (
          <Box sx={{ textAlign: "center", padding: "8px 4px" }}>
            <CheckIcon />
            <Typography sx={{ fontSize: "19px", fontWeight: 700, color: "#22223b", marginBottom: "10px" }}>
              Report sent for interpretation
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#66647a", marginBottom: "18px", lineHeight: 1.5 }}>
              ₹{amountPaid} paid. {creditsUsed} credit used to send this report;{" "}
              {creditsAdded} credit added to your balance.
            </Typography>
            <Box
              sx={{
                display: "inline-block",
                backgroundColor: "#f4f2fa",
                borderRadius: "999px",
                padding: "6px 16px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#4c4a5c",
                marginBottom: "20px",
              }}
            >
              New balance: {newBalance} credit{newBalance === 1 ? "" : "s"}
            </Box>
            <Button
              fullWidth
              variant="contained"
              onClick={onPrimaryAction || onClose}
              sx={{
                height: "44px",
                borderRadius: "9px",
                backgroundColor: "#6c45ff",
                color: "#fff",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#5d38eb", boxShadow: "none" },
              }}
            >
              Done
            </Button>
          </Box>
        );

      case "paymentFailed":
        return (
          <Box sx={{ textAlign: "center", padding: "8px 4px" }}>
            <WarningIcon color="#c62828" bg="#fdecec" />
            <Typography sx={{ fontSize: "19px", fontWeight: 700, color: "#22223b", marginBottom: "10px" }}>
              Payment failed
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#66647a", marginBottom: "22px", lineHeight: 1.5 }}>
              Your payment could not be completed and no credits were
              charged. Please try again.
            </Typography>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={onSecondaryAction || onClose}
                sx={{
                  height: "44px",
                  borderRadius: "9px",
                  borderColor: "#deddea",
                  color: "#4c4a5c",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#c9c5dd", backgroundColor: "#f8f7fc" },
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={onPrimaryAction}
                sx={{
                  height: "44px",
                  borderRadius: "9px",
                  backgroundColor: "#6c45ff",
                  color: "#fff",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#5d38eb", boxShadow: "none" },
                }}
              >
                Retry payment
              </Button>
            </Box>
          </Box>
        );

      case "paidButSendFailed":
        return (
          <Box sx={{ textAlign: "center", padding: "8px 4px" }}>
            <WarningIcon color="#b8860b" bg="#fff3d6" />
            <Typography sx={{ fontSize: "19px", fontWeight: 700, color: "#22223b", marginBottom: "10px" }}>
              Payment received, but the report didn't send
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#66647a", marginBottom: "18px", lineHeight: 1.5 }}>
              We've added your purchased credits to your balance (no credit
              was consumed). You can try sending the report again.
            </Typography>
            <Box
              sx={{
                display: "inline-block",
                backgroundColor: "#f4f2fa",
                borderRadius: "999px",
                padding: "6px 16px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#4c4a5c",
                marginBottom: "20px",
              }}
            >
              Balance: {currentBalance} credits
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Button
                fullWidth
                variant="contained"
                onClick={onPrimaryAction}
                sx={{
                  height: "44px",
                  borderRadius: "9px",
                  backgroundColor: "#6c45ff",
                  color: "#fff",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#5d38eb", boxShadow: "none" },
                }}
              >
                Try sending again
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={onSecondaryAction || onClose}
                sx={{
                  height: "44px",
                  borderRadius: "9px",
                  borderColor: "#deddea",
                  color: "#4c4a5c",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#c9c5dd", backgroundColor: "#f8f7fc" },
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  // topUpSuccess renders as a lightweight toast-style banner, not a full dialog
  if (status === "topUpSuccess") {
    return open ? (
      <Box sx={{ maxWidth: "420px" }}>{renderContent()}</Box>
    ) : null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "420px",
          maxWidth: "calc(100% - 32px)",
          borderRadius: "14px",
          padding: "20px 22px",
          margin: "16px",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
        },
      }}
    >
      <DialogContent sx={{ padding: "0 !important" }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentStatusModal;